import React, { useState, useEffect, useContext } from 'react';
import { isEmpty } from 'lodash';
import { useQuery } from '@apollo/client';
import { FETCH_CLIENTS } from './queriesMutations';
import { Autocomplete } from '../../2-component';
import { UserContext } from '../../../providers/userContextProvider';
import { SubTradesDataUsers, SubTradeSelectedUser, SubTradesSearchUser } from './subtradesPage/subTradesTypes';

type UsersAutocompleteProps = {
  onChange: (user: SubTradeSelectedUser) => void
};

function UsersAutocomplete({ onChange }: UsersAutocompleteProps) {
  const [value, setValue] = useState<SubTradeSelectedUser | ''>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [debouncedValue, setDebouncedValue] = useState<string>('');
  const [clientList, setClientList] = useState<SubTradesSearchUser[]>([]);
  const { activeOrganization, activeCurrency } = useContext(UserContext);

  const perPage = 10;
  const page = 1;

  const { loading, refetch } = useQuery(FETCH_CLIENTS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        pagination: {
          perPage,
          page,
        },
        filter: {
          organizationId: activeOrganization.id,
          query: debouncedValue,
          types: ['INDIVIDUAL'],
        },
      },
      currency: activeCurrency || 'CAD',
    },
    onCompleted: (response: SubTradesDataUsers) => {
      setClientList(response?.searchUsers?.users || []);
    },
  });

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 300);

    return () => clearTimeout(timerId);
  }, [inputValue]);

  useEffect(() => {
    refetch({
      input: {
        pagination: {
          perPage,
          page,
        },
        filter: {
          organizationId: activeOrganization.id,
          query: debouncedValue,
          types: ['INDIVIDUAL'],
        },
      },
      currency: activeCurrency || 'CAD',
    });
  }, [debouncedValue, refetch, activeCurrency, activeOrganization.id]);

  return (
    <Autocomplete
      fullWidth
      label='Clients'
      value={value}
      options={clientList?.map((user) => ({ label: `${user.firstName} ${user.lastName}`, id: user.id }))}
      isOptionEqualToValue={(option: any, currValue: any) => option?.id === currValue?.id}
      loading={loading}
      onInputChange={(event: any, newValue: string) => {
        setInputValue(newValue);
      }}
      onChange={(event: any, newValue: SubTradeSelectedUser) => {
        setValue(newValue);
        onChange(newValue);
      }}
      getOptionLabel={(option) => {
        if (!isEmpty(option)) { return option.label; }
        return '';
      }}
    />
  );
}

export default UsersAutocomplete;
